import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { delay, distinctUntilChanged } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class SpinnerOverlayService {
  private activeRequests = 0;
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);

  /**
   * Set to true in the browser console to trace every show/hide:
   *     window.spinnerDebug = true
   * Useful for finding which request is holding the overlay open.
   */
  private get debug(): boolean {
    return (globalThis as any).spinnerDebug === true;
  }

  /**
   * Current loading state.
   *
   * delay(0) defers the emission by a microtask so overlay toggling never
   * lands mid-change-detection. This is what prevented NG0100 -- keep it.
   *
   * distinctUntilChanged() stops the overlay being torn down and rebuilt when
   * several short requests overlap.
   */
  public readonly isLoading$: Observable<boolean> = this.loadingSubject
    .asObservable()
    .pipe(distinctUntilChanged(), delay(0));

  public show(): void {
    this.activeRequests++;
    if (this.debug) {
      console.log(`[Spinner] show -> ${this.activeRequests}`);
    }
    if (this.activeRequests === 1) {
      this.loadingSubject.next(true);
    }
  }

  public hide(): void {
    if (this.activeRequests > 0) {
      this.activeRequests--;
    }
    if (this.debug) {
      console.log(`[Spinner] hide -> ${this.activeRequests}`);
    }

    if (this.activeRequests === 0 && this.loadingSubject.value) {
      this.loadingSubject.next(false);
    }
  }

  /**
   * Hard reset. Still called externally from
   * quick-checkout-order.component.ts, so this stays public.
   *
   * Safe to call on NavigationStart: requests belonging to the outgoing page
   * are cancelled by the router without necessarily completing, and their
   * results are irrelevant to the page being navigated to.
   *
   * Do NOT call this on a timer. That is what the old 60-second failsafe did,
   * and it desyncs the counter against requests that are still legitimately in
   * flight. The interceptor's per-request watchdog handles hung requests
   * correctly instead.
   */
  public reset(): void {
    if (this.debug && this.activeRequests > 0) {
      console.warn(`[Spinner] reset, discarding ${this.activeRequests} pending`);
    }
    this.activeRequests = 0;
    if (this.loadingSubject.value) {
      this.loadingSubject.next(false);
    }
  }

  /** Number of requests currently holding the overlay open. */
  public get pendingCount(): number {
    return this.activeRequests;
  }
}